/**
 * Ways back and forward between a book, its print review, its order and Help, built only from
 * identities this client has checked: a book id the reader's own list confirmed, an order the
 * server returned for that book, and a reading page that is a plain page number. Never a return
 * address taken from a link.
 */

const ULID = /^[0-9A-HJKMNP-TV-Z]{26}$/u;

/** A page number a link may carry: the page the reader was on, 1–999; the cover needs none. */
export function readingPage(value: string | null | undefined): number | undefined {
  if (!value || !/^\d{1,3}$/u.test(value)) return undefined;
  const page = Number(value);
  return page >= 1 && page <= 999 ? page : undefined;
}

/** A book id shaped as the service issues them; anything else matches no book (D14). */
export function bookIdentity(value: string | null | undefined): string | undefined {
  return value && ULID.test(value) ? value : undefined;
}

/** An order reference shaped as the service issues them; anything else is ignored. */
export function orderIdentity(value: string | null | undefined): string | undefined {
  return value && ULID.test(value) ? value : undefined;
}

const withPage = (page?: number) => (page ? `page=${page}` : "");
/** The step of making a book, in the customer's words: it travels to Help as navigation only. */
const withStep = (step?: string) => {
  const clean = step?.replace(/\s+/gu, " ").trim().slice(0, 60);
  return clean ? `step=${encodeURIComponent(clean)}` : "";
};
export type ReadingSelection = {
  view: "ACCEPTED" | "WORKING";
  editionId?: string | null;
  proofId?: string | null;
};

/** Navigation context only. The reader must verify these identities with the server. */
export function readingSelection(query: URLSearchParams): ReadingSelection | undefined {
  const view = query.get("readerView") ?? query.get("view");
  if (view !== "ACCEPTED" && view !== "WORKING") return undefined;
  return {
    view,
    ...(view === "ACCEPTED" && query.get("edition") ? { editionId: query.get("edition") } : {}),
    ...(view === "WORKING" && query.get("proof") ? { proofId: query.get("proof") } : {}),
  };
}
const selectionParts = (selection?: ReadingSelection) => [
  selection ? `view=${selection.view}` : "",
  selection?.view === "ACCEPTED" && selection.editionId
    ? `edition=${encodeURIComponent(selection.editionId)}`
    : "",
  selection?.view === "WORKING" && selection.proofId
    ? `proof=${encodeURIComponent(selection.proofId)}`
    : "",
];
// Help's own `view=new` must not collide with the edition chosen in the reader.
const helpSelectionParts = (selection?: ReadingSelection) =>
  selectionParts(selection).map((part) =>
    part.startsWith("view=") ? part.replace("view=", "readerView=") : part,
  );
const query = (...parts: string[]) => {
  const filled = parts.filter(Boolean);
  return filled.length ? `?${filled.join("&")}` : "";
};

/** The book in My Library, open at the page the reader left, or at its cover. */
export function bookHref(bookId: string, page?: number, selection?: ReadingSelection) {
  return `/app${query(
    `book=${encodeURIComponent(bookId)}`,
    withPage(page),
    ...selectionParts(selection),
  )}`;
}

/** The book's exact print review, remembering the page it was opened from. */
export function printReviewHref(bookId: string, page?: number, selection?: ReadingSelection) {
  return `/app/books/${encodeURIComponent(bookId)}/print-review${query(withPage(page), ...selectionParts(selection))}`;
}

/** An order's progress, remembering the reading page its book was left at. */
export function orderHref(orderId: string, page?: number, selection?: ReadingSelection) {
  return `/app/physical-orders/${encodeURIComponent(orderId)}${query(withPage(page), ...selectionParts(selection))}`;
}

/**
 * Help about a printed copy: book, page and order are navigation context until the server
 * verifies the customer's own order of that book. Only the confirmed receipt proves that the
 * order was attached; rejected context is never presented as attached.
 */
export function printHelpHref(
  bookId: string,
  options: { page?: number; orderId?: string; selection?: ReadingSelection; step?: string } = {},
) {
  return `/support${query(
    "view=new",
    "topic=print",
    `book=${encodeURIComponent(bookId)}`,
    withPage(options.page),
    withStep(options.step),
    options.orderId ? `order=${encodeURIComponent(options.orderId)}` : "",
    ...helpSelectionParts(options.selection),
  )}`;
}

/** A book question keeps the reading position, never an arbitrary return URL. */
export function bookHelpHref(
  bookId: string,
  page?: number,
  selection?: ReadingSelection,
  step?: string,
) {
  return `/support${query("view=new", "topic=book", `book=${encodeURIComponent(bookId)}`, withPage(page), withStep(step), ...helpSelectionParts(selection))}`;
}

/**
 * Help from wherever someone is making a book (F8): about that book, at that page and step. With
 * no book in hand it is Help itself.
 */
export function taskHelpHref(
  task?: { bookId?: string; page?: number; step?: string; helpStep?: string } | null,
) {
  return task?.bookId && ULID.test(task.bookId)
    ? bookHelpHref(task.bookId, task.page, undefined, task.helpStep ?? task.step)
    : "/support";
}

/** The step of making a book a Help link named, as Help may show and send it. */
export function helpStep(value: string | null | undefined): string | undefined {
  const clean = value?.replace(/\s+/gu, " ").trim().slice(0, 60);
  return clean && /^[\p{L}\p{N}][\p{L}\p{N} ’'&,.:()·-]*$/u.test(clean) ? clean : undefined;
}

/** The short reference support and emails use for an order: its last eight characters. */
export function orderReference(orderId: string) {
  return orderId.slice(-8).toUpperCase();
}
